import {
  GQL_ALTER_MY_COLLECTION,
  GQL_GET_COLLECTION,
  GQL_GET_COLLECTIONS,
  GQL_GET_MY_COLLECTIONS,
  GQL_REMOVE_COLLECTION,
  GQL_UPDATE_COLLECTION,
} from "@/graphql/bundle.graphql";
import {
  IAlterCollectionAction,
  ICollection,
  ICollectionOrderField,
} from "@/interfaces/bundle.interface";
import { Order } from "@/interfaces/common.interface";
import { collectionsState, collectionState } from "@/stores/collection";
import { shortUUID } from "@/utils/string.util";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import { message } from "antd";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import useTranslation from "./useTranslation";

export const useCollections = (props: {
  page: number;
  size: number;
  field?: ICollectionOrderField;
  ordered?: Order;
}) => {
  const { page, size } = props;
  const [field, setField] = useState(ICollectionOrderField.UPDATED_TIME);
  const setCollections = useSetRecoilState(collectionsState);
  const { data, loading, refetch: _refetch } = useQuery<
    { getCollections: { count: number; results: ICollection[] } },
    {
      skip?: number;
      take?: number;
      field?: ICollectionOrderField;
      ordered?: Order;
    }
  >(GQL_GET_COLLECTIONS, {
    variables: { skip: (page - 1) * size, take: size, field },
  });

  useMemo(() => {
    if (data) {
      setCollections(data.getCollections);
    }
  }, [data]);

  const refetch = async () => {
    const { data } = await _refetch({
      skip: (page - 1) * size,
      take: size,
      field,
    });
    setCollections(data?.getCollections);
  };

  return {
    loading,
    refetch,
    field,
    setField,
  };
};

export const useMyCollection = () => {
  const [_getMyCollection, { data, loading, refetch: _refetch }] = useLazyQuery<
    {
      getMyCollections: {
        count: number;
        results: { name: string; id: string }[];
      };
    },
    { take?: number; skip?: number }
  >(GQL_GET_MY_COLLECTIONS);

  const _take = 5;

  const getMyCollection = (skip: number, take = _take) =>
    _getMyCollection({ variables: { skip, take } });

  const refetch = (skip: number, take = _take) => _refetch({ take, skip });

  return {
    loading,
    getMyCollection,
    refetch,
    myCollection: data?.getMyCollections?.results,
    count: data?.getMyCollections?.count,
    take: _take,
  };
};

export const useGetCollection = ({
  page,
  size,
  id,
}: {
  page: number;
  size: number;
  id: string;
}) => {
  const [collection, setCollection] = useRecoilState(collectionState);
  const { data, loading, refetch: _refetch } = useQuery<
    { getCollection: ICollection },
    { take: number; skip: number; id: string }
  >(GQL_GET_COLLECTION, {
    variables: { take: size, skip: (page - 1) * size, id },
    fetchPolicy: "cache-and-network",
  });

  const gLoading = useMemo(() => {
    if (collection.papers?.length > 0) {
      return false;
    }
    return loading;
  }, [loading, collection]);

  useMemo(() => {
    if (data) {
      setCollection(data?.getCollection);
    }
  }, [data]);

  const refetch = async () => {
    const { data } = await _refetch({
      take: size,
      skip: (page - 1) * size,
      id,
    });
    if (data) {
      setCollection(data?.getCollection);
    }
  };

  const router = useRouter();

  const handleChangePage = async (props: { page?: number; size?: number }) => {
    const _page = props.page ?? page;
    const _size = props.size ?? size;
    await router.push(
      `/bundle/collection/[id]?page=${_page}&size=${_size}`,
      `/bundle/collection/${shortUUID(id)}?page=${_page}&size=${_size}`
    );
  };

  return {
    gLoading,
    loading,
    handleChangePage,
    refetch,
  };
};

export const useUpdateCollection = () => {
  const [_updateCollection, { data, loading }] = useMutation<
    { updateCollection: ICollection },
    {
      ucd: {
        collectionId: string;
        public?: boolean;
        name?: string;
        description?: string;
      };
    }
  >(GQL_UPDATE_COLLECTION);

  const [collection, setCollection] = useRecoilState(collectionState);

  const updateCollection = (props: {
    collectionId: string;
    public?: boolean;
    name?: string;
    description?: string;
  }) => _updateCollection({ variables: { ucd: props } });

  useMemo(() => {
    if (data) {
      setCollection(data?.updateCollection);
    }
  }, [data]);

  return { loading, updateCollection };
};

export const useRemoveCollection = () => {
  const [_removeCollection, { loading }] = useMutation<
    { removeCollection: boolean },
    { id: string }
  >(GQL_REMOVE_COLLECTION);

  const router = useRouter();

  const { t } = useTranslation("bundle");

  const removeCollection = async (id: string) => {
    const { data } = await _removeCollection({ variables: { id } });
    if (data?.removeCollection) {
      message.success(t("collection.page.delete-success"));
      router.push("/bundle/collection");
    }
  };

  return { removeCollection, loading };
};

export const useAlterCollection = () => {
  const [_alterCollection, { data, loading }] = useMutation<
    {},
    {
      acd: {
        collectionId: string;
        action: IAlterCollectionAction;
        paperIds: string[];
      };
    }
  >(GQL_ALTER_MY_COLLECTION);

  const alterCollection = async ({
    collectionId,
    action = IAlterCollectionAction.ADD,
    paperIds,
  }: {
    collectionId: string;
    action?: IAlterCollectionAction;
    paperIds: string[];
  }) => {
    await _alterCollection({
      variables: {
        acd: { collectionId, action, paperIds: paperIds.filter((e) => e) },
      },
    });
  };

  return {
    alterCollection,
    loading,
  };
};
